import { Link } from '@tanstack/react-router';
import { Check, HelpCircle } from 'lucide-react';

import type { InvoiceExtractionOut, InvoiceLinkOut } from '@/api/invoices';
import { Badge } from '@/components/ui/badge';
import { requiredInvoiceFieldsComplete } from '@/lib/invoiceCompleteness';
import { formatInvoiceEffectiveRate } from '@/lib/invoiceEffectiveRate';
import { cn } from '@/lib/utils';

/** 📎 chip for a transaction's attached invoice documents.
 *
 *  Renders nothing when the transaction has no links — absence of the
 *  chip IS the "no invoice" state.
 *
 *  Full variant opens a hover/focus preview with the captured facts +
 *  a link into the invoice manager; `compact` renders just the badge
 *  with a filename tooltip for dense table cells (whose overflow
 *  containers would clip an absolutely-positioned panel).
 */
export function AttachmentChip({
  links,
  customerSlug,
  compact = false,
  extractions,
}: {
  links: InvoiceLinkOut[] | undefined;
  customerSlug: string | null;
  compact?: boolean;
  /** Document-level facts (label-first flow) keyed by document id —
   *  preferred over the legacy per-link fact columns when present. */
  extractions?: Map<string, InvoiceExtractionOut>;
}) {
  if (!links || links.length === 0) return null;

  const label = links.length > 1 ? `📎 ${links.length}` : '📎';
  const chipClass = 'border-brand-royal/30 bg-brand-royal/5 text-brand-navy';
  const filenames = links.map((l) => l.filename ?? 'invoice').join(', ');
  const fieldsComplete = requiredInvoiceFieldsComplete(links, extractions);

  if (compact) {
    return (
      <Badge variant="outline" className={cn('font-normal', chipClass)} title={filenames}>
        {label}
        <CompletenessMark complete={fieldsComplete} />
      </Badge>
    );
  }

  return (
    // tabIndex puts the chip in the tab order; focus-within keeps the
    // panel open while keyboard users tab onto it or into its link.
    <div className="group relative inline-flex flex-wrap items-center gap-1.5" tabIndex={0}>
      <Badge variant="outline" className={cn('cursor-default font-normal', chipClass)}>
        {label} invoice{links.length > 1 ? 's' : ''}
        <CompletenessMark complete={fieldsComplete} />
      </Badge>
      <div className="invisible absolute left-0 top-full z-20 mt-1 w-96 rounded-md border border-border bg-popover p-3 text-xs text-popover-foreground shadow-md group-hover:visible group-focus-within:visible">
        <div className="grid gap-3">
          {links.map((link) => (
            <AttachmentPreviewRow
              key={link.id}
              link={link}
              extraction={extractions?.get(link.source_document_id)}
            />
          ))}
        </div>
        {customerSlug && (
          <Link
            to="/customers/$slug"
            params={{ slug: customerSlug }}
            search={{ tab: 'invoices' }}
            className="mt-3 block font-medium text-brand-royal underline-offset-4 hover:underline"
          >
            Manage in invoice pool →
          </Link>
        )}
      </div>
    </div>
  );
}

function AttachmentPreviewRow({
  link,
  extraction,
}: {
  link: InvoiceLinkOut;
  extraction?: InvoiceExtractionOut;
}) {
  // Document-level label wins; legacy link facts are the fallback for
  // pre-labelling assignments.
  const source = extraction ?? link;
  const facts: string[] = [];
  if (extraction?.document_type && extraction.document_type !== 'invoice')
    facts.push(extraction.document_type.replaceAll('_', ' '));
  if (source.invoice_number) facts.push(`#${source.invoice_number}`);
  if (source.invoice_date) facts.push(source.invoice_date);
  if (source.invoice_total) facts.push(`total ${source.invoice_total}`);
  if (source.tax_shown) facts.push(`tax ${source.tax_shown}`);
  const effectiveRate = formatInvoiceEffectiveRate(source.tax_shown, source.invoice_total);
  if (effectiveRate) facts.push(`eff ${effectiveRate}`);
  if (source.supplier_name_on_invoice) facts.push(source.supplier_name_on_invoice);

  return (
    <div className="grid gap-1 border-b border-border/40 pb-2 last:border-b-0 last:pb-0">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium text-brand-navy">
          {link.filename ?? 'invoice'}
        </span>
      </div>
      {facts.length > 0 && <p className="text-brand-slate">{facts.join(' · ')}</p>}
      {extraction && (
        <p
          className={cn(
            'text-[11px]',
            extraction.verdict.complete ? 'text-emerald-700' : 'text-amber-800',
          )}
        >
          {extraction.verdict.supportable
            ? `tier ${extraction.verdict.tier ?? '?'} · ${
                extraction.verdict.complete
                  ? 'docs complete'
                  : `${extraction.verdict.missing_fields.length} field(s) missing`
              }`
            : 'cannot support an ITC (document type)'}
          {extraction.verdict.simplified_registry_bn && ' · RT9999 blocked'}
        </p>
      )}
      {link.notes && <p className="italic text-brand-slate">{link.notes}</p>}
    </div>
  );
}

/** Field-completeness indicator on the invoice tag: a green check when
 *  the required invoice fields (just the tax amount for now) are all
 *  captured, a purple question mark when they're not. This is about
 *  whether the info was entered, not a tax-paid judgment. */
function CompletenessMark({ complete }: { complete: boolean }) {
  return (
    <span
      className="inline-flex items-center"
      title={
        complete
          ? 'Required invoice fields captured (tax amount)'
          : 'Required invoice fields missing (tax amount)'
      }
    >
      {complete ? (
        <Check
          size={12}
          strokeWidth={3}
          className="text-emerald-600"
          aria-label="Required invoice fields captured"
        />
      ) : (
        <HelpCircle
          size={12}
          strokeWidth={2.5}
          className="text-purple-600"
          aria-label="Required invoice fields missing"
        />
      )}
    </span>
  );
}
